import React, {forwardRef, useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useGoogleMapsApiLoader} from '../../google-maps-api'
import {Box, Input, Skeleton, useMergeRefs} from '@chakra-ui/react'
import logger from '../../utils/logger-instance'
import useEffectEvent from '../../hooks/use-effect-event'

const PlaceAutocompleteField = forwardRef(
    (
        {
            value,
            onChangeValue,
            onPlacePrediction,
            options,
            hostProps,
            minHeight = '3rem',
            placeholder
        },
        ref
    ) => {
        const loadApi = useGoogleMapsApiLoader()
        const hostRef = useRef(null)
        const elRef = useRef(null)
        const [ready, setReady] = useState(false)

        const setWidgetRef = useMergeRefs(elRef, ref)

        const handler = useEffectEvent((e) => {
            // some environments dispatch the event with payload nested in `detail`
            const placePrediction = e?.placePrediction ?? e?.detail?.placePrediction
            if (!placePrediction) return

            onPlacePrediction?.(placePrediction)
            onChangeValue?.(elRef.current?.value || '')
        })

        useEffect(() => {
            if (typeof window === 'undefined') return
            let cancelled = false
            let widget = null

            const init = async () => {
                await loadApi()
                if (cancelled) return

                const host = hostRef.current
                if (!host) return

                widget = new window.google.maps.places.PlaceAutocompleteElement(options || {})
                widget.style.display = 'block'
                widget.style.width = '100%'
                widget.style.colorScheme = 'light'
                widget.value = value ?? ''

                if (placeholder) widget.placeholder = placeholder

                host.appendChild(widget)
                widget.addEventListener('gmp-select', handler)
                setWidgetRef(widget)
                setReady(true)
            }

            init().catch((e) => {
                logger.warn('PlaceAutocompleteElement cannot be initialized', e)
            })

            return () => {
                cancelled = true
                if (widget) {
                    widget.removeEventListener('gmp-select', handler)
                    widget.remove?.()
                }

                setWidgetRef(null)
                setReady(false)
            }
        }, [loadApi, options, value, handler, setWidgetRef, placeholder])

        // Update widget value when the provided value changes
        useEffect(() => {
            const el = elRef.current
            if (!el) return
            const next = value ?? ''
            if (el.value !== next) el.value = next
        }, [value])

        return (
            <Box position="relative" width="100%" minH={minHeight}>
                <Input
                    as="div"
                    ref={hostRef}
                    width="100%"
                    opacity={ready ? 1 : 0}
                    pointerEvents={ready ? 'auto' : 'none'}
                    data-testid={'place-autocomplete-host'}
                    variant="accepted"
                    padding={0}
                    {...hostProps}
                />

                {!ready && (
                    <Skeleton position="absolute" inset={0} height={minHeight} borderRadius="md" />
                )}
            </Box>
        )
    }
)

PlaceAutocompleteField.displayName = 'PlaceAutocompleteField'

PlaceAutocompleteField.propTypes = {
    value: PropTypes.string,
    minHeight: PropTypes.string,
    placeholder: PropTypes.string,
    onChangeValue: PropTypes.func,
    onPlacePrediction: PropTypes.func,
    options: PropTypes.object,
    hostProps: PropTypes.object
}

export default PlaceAutocompleteField
